import React, { useMemo, useState } from 'react';
import Heatmap, { HeatmapLegend } from './Heatmap';
import { useT } from '../../i18n';
import PieChart from './PieChart';
import ViewToggle from './ViewToggle';
import { FadeIn } from './Loading';
import { formatPct } from './timeframes';

// Allocation, grouped by a dimension the reader picks, shown three ways.
//
// Sector is the dimension that was asked for and it leads the list. The other
// three come straight off the position, so this stays useful if sector
// enrichment is ever unavailable.
//
// The three views answer different questions and none of them is redundant:
// bars rank and give exact figures, the donut shows concentration at a glance,
// the heatmap adds P/L as a second dimension that neither of the others can.

const DIMENSIONS = [
    { key: 'sector', labelKey: 'breakdown.sector' },
    { key: 'assetType', labelKey: 'breakdown.assetType' },
    { key: 'country', labelKey: 'breakdown.country' },
    { key: 'currency', labelKey: 'breakdown.currency' },
];

const VIEWS = [
    ['bars', 'breakdown.bars'],
    ['pie', 'breakdown.pie'],
    ['heatmap', 'breakdown.heatmap'],
];

/** The bucket KEY for anything the provider could not classify. Stays English
 *  because it is an identifier the grouping keys on; the display name is
 *  translated at render. */
const UNCLASSIFIED = 'Unclassified';

export default function AllocationBreakdown({ positions, baseCurrency, defaultOpen = false }) {
    const { t, td, fmt } = useT();
    const [dimension, setDimension] = useState('sector');
    const [view, setView] = useState('bars');
    // Folded away by default: the overview leads with the four sections.
    const [open, setOpen] = useState(defaultOpen);

    const groups = useMemo(() => {
        const totals = new Map();
        let total = 0;

        for (const position of positions) {
            const value = Number(position.marketValueBase ?? 0);
            if (!Number.isFinite(value) || value === 0) continue;

            const key = position[dimension] || UNCLASSIFIED;
            const bucket = totals.get(key) ?? { value: 0, pnl: 0, cost: 0 };
            bucket.value += value;

            // Accumulate cost as well as P/L so the group's percentage change is
            // value-weighted. Averaging each holding's percentage would let a
            // tiny position up 300% dominate a sector it barely occupies.
            const pnl =
                position.unrealizedPnlBase === null || position.unrealizedPnlBase === undefined
                    ? null
                    : Number(position.unrealizedPnlBase);
            if (pnl !== null && Number.isFinite(pnl)) {
                const cost =
                    position.costBasis !== null && position.costBasis !== undefined && Number(position.costBasis) !== 0
                        ? Number(position.costBasis)
                        : value - pnl;
                if (cost > 0) {
                    bucket.pnl += pnl;
                    bucket.cost += cost;
                }
            }

            totals.set(key, bucket);
            total += value;
        }

        if (total === 0) return [];

        return [...totals.entries()]
            .map(([label, b]) => ({
                label,
                value: b.value,
                share: (b.value / total) * 100,
                changePct: b.cost > 0 ? (b.pnl / b.cost) * 100 : null,
            }))
            .sort((a, b) => b.value - a.value);
    }, [positions, dimension]);

    if (groups.length === 0) return null;

    const allUnclassified = groups.length === 1 && groups[0].label === UNCLASSIFIED;
    const dimensionLabel = t(DIMENSIONS.find((d) => d.key === dimension).labelKey).toLowerCase();

    /** Sector and asset type arrive as data and have their own catalogue;
     *  country and currency are codes and stay as they are. */
    const groupLabel = (label) =>
        label === UNCLASSIFIED ? t('breakdown.unclassified') : td(dimension, label);

    const money = (n) => fmt.number(n, 0);

    return (
        <div className="alloc">
            <div className="alloc__head">
                <h3 className="alloc__title">
                    <button
                        type="button"
                        className="alloc__toggle"
                        aria-expanded={open}
                        onClick={() => setOpen((v) => !v)}
                    >
                        {t('breakdown.title')}
                        <span className={`alloc__chevron${open ? ' is-open' : ''}`} aria-hidden="true">▾</span>
                    </button>
                </h3>
                {open && (
                    <div className="alloc__controls">
                        <div className="alloc__dims">
                            {DIMENSIONS.map((d) => (
                                <button
                                    key={d.key}
                                    type="button"
                                    className={`alloc__dim${dimension === d.key ? ' is-active' : ''}`}
                                    onClick={() => setDimension(d.key)}
                                >
                                    {t(d.labelKey)}
                                </button>
                            ))}
                        </div>
                        <ViewToggle
                            ariaLabel={t('breakdown.aria')}
                            options={VIEWS.map(([k, key]) => [k, t(key)])}
                            value={view}
                            onChange={setView}
                        />
                    </div>
                )}
            </div>

            {!open ? null : allUnclassified ? (
                <p className="alloc__note">{t('breakdown.noData', { dimension: dimensionLabel })}</p>
            ) : (
                <FadeIn viewKey={`${dimension}-${view}`}>
                    {view === 'bars' && (
                        <ul className="alloc__list">
                            {groups.map((group) => (
                                <li key={group.label} className="alloc__row">
                                    <span className="alloc__label">{groupLabel(group.label)}</span>
                                    <span className="alloc__bar">
                                        <span
                                            className="alloc__fill"
                                            style={{ width: `${group.share.toFixed(1)}%` }}
                                        />
                                    </span>
                                    <span className="alloc__share">{group.share.toFixed(1)}%</span>
                                    {/* Unrealised P/L against cost — what the
                                        heatmap shows as colour, as a figure. */}
                                    <span
                                        className={`alloc__pnl${
                                            group.changePct > 0 ? ' is-positive'
                                                : group.changePct < 0 ? ' is-negative' : ''
                                        }`}
                                        title={t('breakdown.pnlTitle')}
                                    >
                                        {formatPct(group.changePct, 1) ?? '—'}
                                    </span>
                                    <span className="alloc__value">
                                        {money(group.value)} {baseCurrency}
                                    </span>
                                </li>
                            ))}
                        </ul>
                    )}

                    {view === 'pie' && <PieChart groups={groups} baseCurrency={baseCurrency} />}

                    {view === 'heatmap' && (
                        <>
                            <Heatmap
                                height={320}
                                tiles={groups.map((g) => ({
                                    key: g.label,
                                    label: groupLabel(g.label),
                                    value: g.value,
                                    changePct: g.changePct,
                                }))}
                            />
                            <HeatmapLegend label={t('breakdown.legend')} />
                        </>
                    )}
                </FadeIn>
            )}
        </div>
    );
}
